import { z } from 'zod'
import screenPromptsJson from '@/ai/screen-prompts.json'
import { STAFF_ROLES } from '@/lib/staff-roles'

// ── Per-screen starter prompts for the copilot bubble (F-AI-50) ─────────────
// `src/ai/screen-prompts.json` maps an admin route prefix to two or three
// prompts the empty bubble offers on that screen. Longest matching prefix
// wins; an unknown admin route gets the fallback (the dashboard set). A
// prompt with `send: false` only seeds the composer, for prompts the staff
// member must finish first (a price change names an item). Validated with zod
// at import like nav-targets.json: a malformed file costs the bubble its
// starters, never the screen.

export const PROMPT_ICONS = ['chart', 'stock', 'receipt', 'users', 'moon', 'help', 'edit'] as const
export type PromptIcon = (typeof PROMPT_ICONS)[number]

const promptSchema = z.object({
  icon: z.enum(PROMPT_ICONS),
  text: z.string().min(1),
  /** Default true: tapping sends the prompt as a message. */
  send: z.boolean().optional(),
})

const screenSchema = z.object({
  route: z.string().startsWith('/admin'),
  /** Roles the set is for; absent means every role the bubble shows for. */
  roles: z.array(z.enum(STAFF_ROLES)).min(1).optional(),
  prompts: z.array(promptSchema).min(1).max(4),
})

export const screenPromptsSchema = z.object({
  version: z.number().int().positive(),
  fallback: z.string().startsWith('/admin'),
  screens: z.array(screenSchema).min(1),
})

export type ScreenPrompt = z.infer<typeof promptSchema>
export type ScreenPrompts = z.infer<typeof screenPromptsSchema>

/** The raw JSON, for the contract test. */
export const SCREEN_PROMPTS_RAW: unknown = screenPromptsJson

const parsed = screenPromptsSchema.safeParse(screenPromptsJson)
if (!parsed.success) {
  console.error('[bhookly-ai] screen-prompts.json failed validation; starters disabled', parsed.error.issues)
}

export const screenPrompts: ScreenPrompts | null = parsed.success ? parsed.data : null

function matches(route: string, pathname: string): boolean {
  return pathname === route || pathname.startsWith(`${route}/`)
}

/**
 * Starter prompts for the screen at `pathname` and the signed-in `role`, from
 * the given registry (the parsed file by default). Empty when nothing fits.
 */
export function promptsFor(pathname: string, role: string, registry: ScreenPrompts | null = screenPrompts): ScreenPrompt[] {
  if (!registry) return []
  const forRole = (s: ScreenPrompts['screens'][number]) => !s.roles || (s.roles as readonly string[]).includes(role)
  let best: ScreenPrompts['screens'][number] | undefined
  for (const s of registry.screens) {
    if (!matches(s.route, pathname) || !forRole(s)) continue
    if (!best || s.route.length > best.route.length) best = s
  }
  if (!best) best = registry.screens.find((s) => s.route === registry.fallback && forRole(s))
  return best ? best.prompts.map((p) => ({ ...p, send: p.send ?? true })) : []
}
